///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { isEmpty, isEqual, map, omit, sortBy } from "lodash";

import CloseIcon from "../../icons/CloseIcon";
import PanelStateIndicator from "../PanelStateIndicator";
import messages from "../../../helpers/messages";
import {
  clearCarriersSelected,
  fetchCarrierMatches,
  saveCarrierMatches,
  enableBulkMatchingOperation,
  setNoneOption,
  setSaveCarrierMatchDetails,
  updateStep
} from "../../../slices/bidProcurementSlice";
import LaneInformation from "./LaneInformation";
import CarrierMatchInformation from "./CarrierMatchInformation";
import UnsavedChangesModal from "./UnsavedChangesModal";
import CarrierMatchingActions from "./CarrierMatchingActions";
import NavigationBlocker from "../../NavigationBlocker";
import SubmissionDetails from "./SubmissionDetails";
import SubmissionPricing from "./SubmissionPricing";
import SystemErrorModal from "../../SystemErrorModal";
import mapLaneMatchesForBulkMatching from "../../../helpers/mapLaneMatchesForBulkMatching";

export const CarrierMatchingDrawer = ({ open, onClose, bidId }) => {
  const dispatch = useDispatch();
  const {
    capacityMatchCarriers: {
      isFetching,
      isError,
      carriers,
      noneOption,
      laneMatches,
      unchangedValues
    },
    capacityMatchLaneSummary: { capacityMatches },
    rowsSelected,
    saveCarrierMatchDetails,
    isBulkMatchingOperationEnabled,
    step
  } = useSelector(state => state.bidProcurement);

  const [unsavedChangesModal, toggleUnsavedChangesModal] = useState(false);

  const areLaneMatchesChanged =
    (!isEmpty(laneMatches) &&
      !isEqual(
        sortBy(
          map(laneMatches, m => omit(m, ["showNotes", "showRateHistory"])),
          ["truckloadLaneId", "carrierId"]
        ),
        sortBy(
          map(unchangedValues.laneMatches, m => omit(m, ["showNotes", "showRateHistory"])),
          ["truckloadLaneId", "carrierId"]
        )
      )) ||
    !isEqual(noneOption, unchangedValues.noneOption);

  const handleClose = close => {
    toggleUnsavedChangesModal(false);
    if (close) {
      dispatch(updateStep(1));
      dispatch(clearCarriersSelected());
      dispatch(setNoneOption(false));
      dispatch(enableBulkMatchingOperation(false));
      onClose();
    }
  };

  const openUnsavedModal = () => {
    areLaneMatchesChanged ? toggleUnsavedChangesModal(true) : handleClose(true);
  };

  const closeErrorModal = () =>
    dispatch(setSaveCarrierMatchDetails({ isFetching: false, isError: false }));

  const saveMatches = () => {
    let updatedLaneMatches = null;
    if (isBulkMatchingOperationEnabled) {
      updatedLaneMatches = mapLaneMatchesForBulkMatching(
        capacityMatches,
        rowsSelected,
        laneMatches,
        noneOption
      );
    }
    dispatch(
      saveCarrierMatches(
        bidId,
        noneOption,
        () => handleClose(true),
        false,
        updatedLaneMatches
      )
    );
  };

  return (
    <>
      <NavigationBlocker
        when={carriers?.some(x => x.checked === true) || noneOption}
        onExit={() => handleClose(true)}
      />
      <UnsavedChangesModal
        open={unsavedChangesModal}
        handleClose={handleClose}
      />
      <SystemErrorModal
        open={open && saveCarrierMatchDetails.isError}
        large
        closeModal={closeErrorModal}
        handleExit={closeErrorModal}
        handleTryAgain={saveMatches}
      />
      <Drawer
        open={open}
        onClose={openUnsavedModal}
        direction="right"
        size={600}
        className="drawer"
        lockBackgroundScroll={true}
      >
        <div className="pt-12 pb-12 items-center header">
          <span className="pl-24">
            {messages.carrierMatching.defaultMessage}
          </span>
          <CloseIcon
            height="16px"
            width="16px"
            className="cursor-pointer mr-16 fr"
            onClick={openUnsavedModal}
          />
        </div>

        {(isFetching || isError) && (
          <div className="box panel">
            <PanelStateIndicator
              iconSize="32px"
              loading={isFetching}
              error={!isFetching && isError}
              handleRefresh={() => dispatch(fetchCarrierMatches(bidId))}
              errorMessage={
                messages.sorrySomethingWentWrongPleaseTryAgain.defaultMessage
              }
            />
          </div>
        )}

        {!isFetching && !isError && (
          <div
            style={{ maxHeight: window.screen.height - 236 }}
            className="overflow-x--scroll scroll"
          >
            {step === 1 ? (
              <>
                <LaneInformation />
                <CarrierMatchInformation />
              </>
            ) : null}

            {step === 2 ? (
              <>
                <LaneInformation />
                <SubmissionDetails />
              </>
            ) : null}

            {step === 3 ? <SubmissionPricing /> : null}
          </div>
        )}

        {!isFetching && !isError && (
          <CarrierMatchingActions
            openUnsavedModal={openUnsavedModal}
            areLaneMatchesChanged={areLaneMatchesChanged}
            handleFinish={saveMatches}
          />
        )}
      </Drawer>
    </>
  );
};

export default CarrierMatchingDrawer;
